/**
 * fetchBusinessWithRetry — Feb 17 2027 (deduped Jun 2026)
 *
 * Small wrapper around `GET /api/businesses/:id` that
 *   1. retries once on transient failures before letting the caller eject, and
 *   2. de-duplicates the burst of identical fetches every plan route used to
 *      fire on first paint (the Hub page, GlobalBreadcrumb, OperatingModeShell
 *      and the Plan page each asked for the same payload — six requests per
 *      load). Concurrent callers now share one in-flight promise, and a very
 *      short TTL absorbs the stragglers that mount a beat later.
 *
 * The retry is intentionally narrow: only 401 / network error / 5xx-gateway
 * blips. A hard 403 or 404 fails fast so the caller can eject on real access
 * issues.
 *
 * Freshness: any non-GET request to `/businesses/:id/...` made through the
 * same api client drops that id from the cache, so a save is never followed by
 * a stale read. Pass `{ force: true }` for a deliberate post-mutation refetch
 * or for a poller that must see every tick.
 *
 * Usage:
 *   const { data } = await fetchBusinessWithRetry(api, id);
 *   const { data } = await fetchBusinessWithRetry(api, id, { force: true });
 */
export function isRetryableBusinessFetchError(e) {
  // No response = network error, CORS blip, aborted request.
  if (!e?.response) return true;
  const s = e.response.status;
  return s === 401 || s === 502 || s === 503 || s === 504;
}

const DEFAULT_BACKOFF_MS = 450;

// Long enough to collapse one page's mount burst, short enough that a founder
// never sees a stale plan after navigating.
const CACHE_TTL_MS = 1500;

const inflight = new Map();
const cache = new Map();
const wired = new WeakSet();

function businessIdFromUrl(url) {
  const m = /\/businesses\/([^/?#]+)/.exec(String(url || ""));
  return m ? m[1] : null;
}

export function invalidateBusiness(id) {
  cache.delete(id);
  inflight.delete(id);
}

export function _resetBusinessCache() {
  cache.clear();
  inflight.clear();
}

// Registered once per api client: every write to a business drops its entry.
function wireInvalidation(api) {
  if (!api?.interceptors?.response || wired.has(api)) return;
  wired.add(api);
  api.interceptors.response.use((res) => {
    const method = String(res?.config?.method || "get").toLowerCase();
    if (method !== "get") {
      const id = businessIdFromUrl(res?.config?.url);
      if (id) invalidateBusiness(id);
    }
    return res;
  });
}

// Shallow copy so one consumer mutating its slice can't surprise another that
// read from the same cached response.
function clone(res) {
  return res && typeof res === "object" && res.data && typeof res.data === "object"
    ? { ...res, data: Array.isArray(res.data) ? [...res.data] : { ...res.data } }
    : res;
}

async function fetchOnce(api, id, backoffMs) {
  try {
    return await api.get(`/businesses/${id}`);
  } catch (e) {
    if (!isRetryableBusinessFetchError(e)) throw e;
    await new Promise((r) => setTimeout(r, backoffMs));
    return api.get(`/businesses/${id}`);
  }
}

export function fetchBusinessWithRetry(
  api,
  id,
  { backoffMs = DEFAULT_BACKOFF_MS, force = false } = {},
) {
  wireInvalidation(api);
  if (force) invalidateBusiness(id);

  if (!force) {
    const hit = cache.get(id);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return Promise.resolve(clone(hit.res));
    const pending = inflight.get(id);
    if (pending) return pending.then(clone);
  }

  const p = fetchOnce(api, id, backoffMs).then(
    (res) => {
      cache.set(id, { at: Date.now(), res });
      inflight.delete(id);
      return res;
    },
    (err) => {
      inflight.delete(id);
      throw err;
    },
  );
  inflight.set(id, p);
  return p.then(clone);
}
